<template>
    <div class="py-4 container-fluid">
        <!-- Alert -->
        <soft-alert v-if="alertTable.state" :color="alertTable.color" class="mb-3" dismissible>
            <span class="text-sm">{{ alertTable.message }}</span>
        </soft-alert>

        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <div class="d-flex justify-content-between align-items-center">
                    <div>
                        <h5 class="mb-0 font-weight-bolder">{{ isEdit ? 'Editar Plan' : 'Nuevo Plan' }}</h5>
                        <p class="text-sm text-secondary mb-0">
                            {{ isEdit ? `Editando: ${form.name}` : 'Crear un nuevo plan de suscripción' }}
                        </p>
                    </div>
                    <router-link :to="{ name: ROUTES.PLANS }">
                        <soft-button variant="outline" color="secondary" size="sm">
                            <i class="fas fa-arrow-left me-1"></i>
                            Volver a Planes
                        </soft-button>
                    </router-link>
                </div>
            </div>
        </div>

        <soft-spinner v-if="loadingDetail" />

        <div v-else class="row">
            <!-- Form Column -->
            <div class="col-lg-8">
                <!-- Basic Info Card -->
                <div class="card mb-4">
                    <div class="card-header pb-0">
                        <h6 class="mb-0 font-weight-bolder">
                            <i class="fas fa-info-circle text-primary me-2"></i>
                            Información del Plan
                        </h6>
                    </div>
                    <div class="card-body">
                        <div class="row g-3">
                            <div class="col-md-6">
                                <label class="form-label fw-semibold text-xs">Nombre <span class="text-danger">*</span></label>
                                <input v-model="form.name" type="text" class="form-control form-control-sm"
                                    placeholder="Ej: Explorer" />
                            </div>
                            <div class="col-md-6">
                                <label class="form-label fw-semibold text-xs">Código <span class="text-danger">*</span></label>
                                <input v-model="form.code" type="text" class="form-control form-control-sm"
                                    placeholder="Ej: EXPLORER" />
                            </div>
                            <div class="col-md-6">
                                <label class="form-label fw-semibold text-xs">Precio (COP)</label>
                                <input v-model="form.price" type="text" class="form-control form-control-sm"
                                    placeholder="Ej: 189000" />
                            </div>
                            <div class="col-md-6">
                                <label class="form-label fw-semibold text-xs">Orden</label>
                                <input v-model="form.order" type="number" class="form-control form-control-sm"
                                    placeholder="Ej: 1" />
                            </div>
                            <div class="col-12">
                                <label class="form-label fw-semibold text-xs">Descripción</label>
                                <textarea v-model="form.description" class="form-control form-control-sm"
                                    rows="3" placeholder="Descripción corta para la landing..."></textarea>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Display Options Card -->
                <div class="card mb-4">
                    <div class="card-header pb-0">
                        <h6 class="mb-0 font-weight-bolder">
                            <i class="fas fa-palette text-warning me-2"></i>
                            Opciones de Visualización
                        </h6>
                    </div>
                    <div class="card-body">
                        <div class="row g-3">
                            <div class="col-md-6">
                                <label class="form-label fw-semibold text-xs">Texto de Agentes</label>
                                <input v-model="form.agents_text" type="text" class="form-control form-control-sm"
                                    placeholder="Ej: 1 Usuario Principal, 5 Agentes" />
                            </div>
                            <div class="col-md-6">
                                <label class="form-label fw-semibold text-xs">Subtítulo</label>
                                <input v-model="form.subtitle" type="text" class="form-control form-control-sm"
                                    placeholder="Ej: Sin cláusulas. Cancela cuando Necesites" />
                            </div>
                            <div class="col-md-6">
                                <label class="form-label fw-semibold text-xs">Texto del Botón</label>
                                <input v-model="form.button_text" type="text" class="form-control form-control-sm"
                                    placeholder="Ej: Iniciar una Prueba Gratuita" />
                            </div>
                            <div class="col-md-6">
                                <label class="form-label fw-semibold text-xs">URL del Botón</label>
                                <input v-model="form.button_url" type="text" class="form-control form-control-sm"
                                    placeholder="https://..." />
                            </div>
                            <div class="col-md-6">
                                <label class="form-label fw-semibold text-xs">Badge</label>
                                <input v-model="form.badge_text" type="text" class="form-control form-control-sm"
                                    placeholder="Ej: Popular, Nuevo" />
                            </div>
                            <div class="col-md-3">
                                <label class="form-label fw-semibold text-xs d-block">Público en Landing</label>
                                <div class="form-check form-switch mt-1">
                                    <input v-model="form.is_public" class="form-check-input" type="checkbox" id="isPublic" />
                                    <label class="form-check-label text-xs" for="isPublic">
                                        {{ form.is_public ? 'Visible' : 'Oculto' }}
                                    </label>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <label class="form-label fw-semibold text-xs d-block">Destacado</label>
                                <div class="form-check form-switch mt-1">
                                    <input v-model="form.featured" class="form-check-input" type="checkbox" id="isFeatured" />
                                    <label class="form-check-label text-xs" for="isFeatured">
                                        {{ form.featured ? 'Sí' : 'No' }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Services Card -->
                <div class="card mb-4">
                    <div class="card-header pb-0">
                        <div class="d-flex justify-content-between align-items-center">
                            <h6 class="mb-0 font-weight-bolder">
                                <i class="fas fa-concierge-bell text-success me-2"></i>
                                Servicios del Plan
                                <span class="badge bg-light text-dark border ms-2">{{ form.services.length }}</span>
                            </h6>
                            <soft-button variant="outline" color="success" size="sm" @click="addService">
                                <i class="fas fa-plus me-1"></i>
                                Agregar Servicio
                            </soft-button>
                        </div>
                    </div>
                    <div class="card-body">
                        <div v-if="form.services.length === 0" class="text-center py-4">
                            <i class="fas fa-concierge-bell text-secondary mb-2 d-block" style="font-size: 2rem; opacity: 0.4;"></i>
                            <p class="text-sm text-secondary mb-0">No hay servicios. Agrega uno para empezar.</p>
                        </div>
                        <div v-else>
                            <div v-for="(service, index) in form.services" :key="index"
                                class="service-row d-flex align-items-center gap-2 mb-2 p-2 rounded">
                                <div class="flex-grow-1">
                                    <input v-model="service.name" type="text"
                                        class="form-control form-control-sm"
                                        placeholder="Nombre del servicio" />
                                </div>
                                <div style="width: 140px;">
                                    <select v-model="service.value" class="form-select form-select-sm">
                                        <option value="true">Incluido</option>
                                        <option value="false">No incluido</option>
                                    </select>
                                </div>
                                <button class="btn btn-sm btn-outline-danger mb-0 px-2 py-1"
                                    @click="removeService(index)" title="Eliminar servicio">
                                    <i class="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Save Button -->
                <div class="d-flex justify-content-end gap-2 mb-4">
                    <router-link :to="{ name: ROUTES.PLANS }">
                        <soft-button variant="outline" color="secondary" size="sm">
                            Cancelar
                        </soft-button>
                    </router-link>
                    <soft-button variant="gradient" size="sm" @click="handleSubmit" :loading="spinner">
                        <i class="fas fa-save me-1"></i>
                        {{ isEdit ? 'Actualizar Plan' : 'Crear Plan' }}
                    </soft-button>
                </div>
            </div>

            <!-- Preview Column -->
            <div class="col-lg-4">
                <div class="card position-sticky" style="top: 1rem;">
                    <div class="card-header pb-0">
                        <h6 class="mb-0 font-weight-bolder">
                            <i class="fas fa-eye text-info me-2"></i>
                            Vista Previa
                        </h6>
                    </div>
                    <div class="card-body">
                        <div class="preview-card p-3 border rounded position-relative"
                            :class="{ 'border-warning': form.featured }">
                            <!-- Badge -->
                            <div v-if="form.badge_text" class="preview-badge">
                                {{ form.badge_text }}
                            </div>

                            <!-- Plan Name -->
                            <h5 class="font-weight-bolder mb-1">{{ form.name || 'Nombre del Plan' }}</h5>
                            <p class="text-xs text-secondary mb-3">{{ form.description || 'Descripción del plan' }}</p>

                            <!-- Price -->
                            <div class="mb-3">
                                <h3 class="font-weight-bolder mb-0 text-primary">
                                    {{ formatPricePreview(form.price) }}
                                </h3>
                                <p class="text-xxs text-secondary mb-0">COP / mes</p>
                            </div>

                            <!-- Agents Text -->
                            <p v-if="form.agents_text" class="text-xs font-weight-bold mb-3">
                                <i class="fas fa-users text-secondary me-1"></i>
                                {{ form.agents_text }}
                            </p>

                            <!-- Services List -->
                            <ul class="list-unstyled mb-3" v-if="form.services.length > 0">
                                <li v-for="(service, i) in form.services" :key="i"
                                    class="text-xs mb-1 d-flex align-items-center">
                                    <i class="me-2"
                                        :class="service.value === 'true' ? 'fas fa-check text-success' : 'fas fa-times text-secondary'"></i>
                                    <span :class="{ 'text-secondary': service.value !== 'true' }">
                                        {{ service.name || 'Servicio sin nombre' }}
                                    </span>
                                </li>
                            </ul>

                            <!-- Button -->
                            <soft-button variant="gradient" class="w-100" size="sm">
                                {{ form.button_text || 'Comenzar' }}
                            </soft-button>

                            <!-- Subtitle -->
                            <p v-if="form.subtitle" class="text-xxs text-secondary text-center mt-2 mb-0">
                                {{ form.subtitle }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import SoftButton from "@/components/SoftButton.vue";
import SoftAlert from "@/components/SoftAlert.vue";
import SoftSpinner from "@/components/SoftSpinner.vue";
import ROUTES from "../../constansRoute";
import { usePlans } from "./usePlans.js";

export default {
    name: "PlanForm",
    components: {
        SoftButton,
        SoftAlert,
        SoftSpinner,
    },
    setup() {
        const route = useRoute();
        const router = useRouter();
        const { spinner, alertTable, getPlanDetail, createPlan, updatePlan, showAlert, formatPrice } = usePlans();

        const isEdit = computed(() => !!route.params.id);
        const loadingDetail = ref(false);

        const form = ref({
            name: '',
            code: '',
            description: '',
            price: '',
            is_public: false,
            featured: false,
            agents_text: '',
            subtitle: '',
            button_text: '',
            button_url: '',
            badge_text: '',
            order: null,
            services: [],
        });

        const addService = () => {
            form.value.services.push({ name: '', value: 'true' });
        };

        const removeService = (index) => {
            form.value.services.splice(index, 1);
        };

        const formatPricePreview = (price) => {
            if (!price || price === '0') return 'Gratis';
            const num = parseInt(price);
            if (isNaN(num)) return '$0';
            return '$' + num.toLocaleString('es-CO');
        };

        const handleSubmit = async () => {
            if (!form.value.name || !form.value.code) {
                showAlert('Nombre y Código son obligatorios', 'danger');
                return;
            }

            const payload = {
                name: form.value.name,
                code: form.value.code,
                description: form.value.description || null,
                price: form.value.price || '0',
                is_public: form.value.is_public,
                featured: form.value.featured,
                agents_text: form.value.agents_text || null,
                subtitle: form.value.subtitle || null,
                button_text: form.value.button_text || null,
                button_url: form.value.button_url || null,
                badge_text: form.value.badge_text || null,
                order: form.value.order ? parseInt(form.value.order) : null,
                services: form.value.services
                    .filter(s => s.name.trim() !== '')
                    .map(s => {
                        const svc = { name: s.name, value: s.value };
                        if (s.id) svc.id = s.id;
                        return svc;
                    }),
            };

            let result;
            if (isEdit.value) {
                result = await updatePlan(route.params.id, payload);
            } else {
                result = await createPlan(payload);
            }

            if (result.success) {
                setTimeout(() => {
                    router.push({ name: ROUTES.PLANS });
                }, 1000);
            }
        };

        onMounted(async () => {
            if (isEdit.value) {
                loadingDetail.value = true;
                const data = await getPlanDetail(route.params.id);
                if (data) {
                    form.value = {
                        name: data.name || '',
                        code: data.code || '',
                        description: data.description || '',
                        price: data.price || '',
                        is_public: !!data.is_public,
                        featured: !!data.featured,
                        agents_text: data.agents_text || '',
                        subtitle: data.subtitle || '',
                        button_text: data.button_text || '',
                        button_url: data.button_url || '',
                        badge_text: data.badge_text || '',
                        order: data.order || null,
                        services: (data.services || []).map(s => ({
                            id: s.id,
                            name: s.name,
                            value: s.value,
                        })),
                    };
                }
                loadingDetail.value = false;
            }
        });

        return {
            ROUTES,
            isEdit,
            loadingDetail,
            form,
            spinner,
            alertTable,
            addService,
            removeService,
            formatPricePreview,
            handleSubmit,
            formatPrice,
            showAlert,
        };
    },
};
</script>

<style lang="scss" scoped>
.service-row {
    background: #f8f9fa;
    transition: background 0.15s ease;

    &:hover {
        background: #e9ecef;
    }
}

.preview-card {
    background: #fff;
    transition: border-color 0.2s ease;

    &.border-warning {
        border-width: 2px !important;
    }
}

.preview-badge {
    position: absolute;
    top: -10px;
    right: 12px;
    background: linear-gradient(310deg, #f5365c, #f56036);
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 0.375rem;
}

.form-control:focus,
.form-select:focus {
    border-color: #ef9a9a;
    box-shadow: 0 0 0 2px rgba(229, 57, 53, 0.12);
}

.form-check-input:checked {
    background-color: #E53935;
    border-color: #E53935;
}
</style>
